import { memo, useRef, useMemo } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Box } from "@mui/material";
import Task from "../../taskManagement/task/Task";
import { DashboardTaskViewModel } from "./TaskWidget.types";
import { getScrollbarStyle } from "@dotlegal/dotlegal-ui-components";
import { sectionBorderColor } from "@common/components/dotLegalSection/DotLegalSection";

interface VirtualizedTaskListProps {
    tasks: Array<DashboardTaskViewModel>;
}

const ESTIMATED_TASK_HEIGHT = 180;
const OVERSCAN_COUNT = 5;

interface VirtualizedTaskItemProps {
    task: DashboardTaskViewModel;
    virtualStart: number;
    measureElement: (el: HTMLDivElement | null) => void;
    dataIndex: number;
}

const VirtualizedTaskItem = memo(function VirtualizedTaskItem({ task, virtualStart, measureElement, dataIndex }: VirtualizedTaskItemProps) {
    return (
        <div
            ref={measureElement}
            data-index={dataIndex}
            style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${virtualStart}px)`,
                paddingBottom: 16,
            }}
        >
            <Task
                activityName={task.name}
                taskName={task.processingActivityName ?? ""}
                description={task.description}
                businessAreas={task.businessAreas}
                responsibles={task.responsibles}
                deadline={task.deadline}
                subTaskCompleted={task.completedSubTasks}
                totalSubTasks={task.totalSubTasks}
                taskStatus={task.laneState}
                isOverdue={task.isOverdue}
                taskId={task.id}
                priority={task.priority}
                processingActivtiyId={task.processingActivityId}
                areas={task.areas}
                customId={task.customId}
                isReadyForApproval={task.isReadyForApproval}
                isAdhocTask={task.isAdHocTask}
                annualWheelActivityId={task.annualWheelActivityId}
                evaluationStatus={task.evaluationStatus}
                evaluation={task.evaluation}
                contractId={task.contractId}
                contractName={task.contractName}
                contractUserFriendlyId={task.contractUserFriendlyId}
            />
        </div>
    );
});

function VirtualizedTaskList({ tasks }: VirtualizedTaskListProps) {
    const parentRef = useRef<HTMLDivElement>(null);

    const virtualizer = useVirtualizer({
        count: tasks.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => ESTIMATED_TASK_HEIGHT,
        overscan: OVERSCAN_COUNT,
    });

    const scrollContainerSx = useMemo(
        () => ({
            height: 0,
            flex: "1 1 auto",
            overflowY: "auto" as const,
            overflowX: "hidden" as const,
            scrollbarGutter: "stable" as const,
            paddingRight: 0.5,
            ...getScrollbarStyle(),
            // The widget is white like the task cards, so the cards get the section border to stand apart. Set here rather
            // than in Task, which the task management board shares.
            "& [data-index] > .MuiPaper-root": { border: `1px solid ${sectionBorderColor}` },
        }),
        []
    );

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <Box ref={parentRef} sx={scrollContainerSx}>
                <div
                    style={{
                        height: virtualizer.getTotalSize(),
                        width: "100%",
                        position: "relative",
                    }}
                >
                    {virtualizer.getVirtualItems().map((virtualRow) => {
                        const task = tasks[virtualRow.index];
                        return (
                            <VirtualizedTaskItem
                                key={task.id}
                                task={task}
                                virtualStart={virtualRow.start}
                                measureElement={virtualizer.measureElement}
                                dataIndex={virtualRow.index}
                            />
                        );
                    })}
                </div>
            </Box>
        </Box>
    );
}

export default memo(VirtualizedTaskList);
